import { Professor } from './types';
import { supabase } from './supabase';

// Cache for click tracking
const CLICK_TRACKING_KEY = 'professor_clicks';
const HIDE_DURATION = 14 * 24 * 60 * 60 * 1000; // 2 weeks in milliseconds

interface ClickTrack {
  professorId: string;
  clicks: number;
  lastClickTime: number;
  hideUntil?: number;
}

// Load click tracking data from localStorage
function getClickTracking(): Record<string, ClickTrack> {
  const stored = localStorage.getItem(CLICK_TRACKING_KEY);
  return stored ? JSON.parse(stored) : {};
}

// Save click tracking data to localStorage
function saveClickTracking(data: Record<string, ClickTrack>) {
  localStorage.setItem(CLICK_TRACKING_KEY, JSON.stringify(data));
}

// Track professor profile click
export function trackProfessorClick(professorId: string) {
  const tracking = getClickTracking();
  const now = Date.now();

  if (!tracking[professorId]) {
    tracking[professorId] = {
      professorId,
      clicks: 1,
      lastClickTime: now
    };
  } else {
    tracking[professorId].clicks += 1;
    tracking[professorId].lastClickTime = now;

    // Hide professor if clicked more than 4 times
    if (tracking[professorId].clicks > 4) {
      tracking[professorId].hideUntil = now + HIDE_DURATION;
    }
  }

  saveClickTracking(tracking);
}

// Check if professor should be hidden
export function shouldHideProfessor(professorId: string): boolean {
  const tracking = getClickTracking();
  const now = Date.now();

  if (tracking[professorId]?.hideUntil && tracking[professorId].hideUntil > now) {
    return true;
  }

  return false;
}

export async function searchProfessors(params: {
  searchTerm?: string;
  department?: string;
  university?: string;
  category?: string;
}): Promise<{ results: Professor[]; error: string | null }> {
  try {
    // Fetch professors from Supabase (which will be populated from Deepnote data)
    const query = supabase
      .from('random_professors')
      .select('*')
      .limit(20);

    // Apply filters
    if (params.university) {
      // More flexible university matching
      const universityTerms = params.university.split(' ');
      if (universityTerms.length > 1) {
        // If multiple words, try to match most significant words
        const significantTerms = universityTerms.filter(term => 
          !['university', 'of', 'the', 'and', 'at', 'in'].includes(term.toLowerCase())
        );
        if (significantTerms.length > 0) {
          query.or(significantTerms.map(term => `university.ilike.%${term}%`).join(','));
        } else {
          query.ilike('university', `%${params.university}%`);
        }
      } else {
        // Single word search
        query.ilike('university', `%${params.university}%`);
      }
    }

    if (params.department) {
      query.ilike('department', `%${params.department}%`);
    }

    if (params.category) {
      query.contains('research_interests', [params.category]);
    }

    if (params.searchTerm) {
      query.or(`name.ilike.%${params.searchTerm}%,research_interests.cs.{${params.searchTerm}}`);
    }

    const { data: professors, error } = await query;

    if (error) throw error;

    // Filter out hidden professors
    const visibleProfessors = professors.filter(
      prof => !shouldHideProfessor(prof.id)
    );

    return {
      results: visibleProfessors,
      error: null
    };
  } catch (error) {
    console.error('Professor search error:', error);
    return {
      results: [],
      error: error instanceof Error ? error.message : 'Failed to search professors'
    };
  }
}